
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Stethoscope, Plus, Edit, Trash2, Eye, Package } from "lucide-react";
import { ServiceForm } from "@/components/professional/ServiceForm";
import { ProductForm } from "@/components/professional/ProductForm";

export const ServicesSection = () => {
  const { user } = useAuth();
  const [isServiceFormOpen, setIsServiceFormOpen] = useState(false);
  const [isProductFormOpen, setIsProductFormOpen] = useState(false);

  const { data: services = [], isLoading: servicesLoading } = useQuery({
    queryKey: ["mobile-services"],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from("professional_services")
        .select("*")
        .eq("professional_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user
  });

  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ["mobile-products"],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from("professional_products")
        .select("*")
        .eq("professional_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user
  });

  if (servicesLoading || productsLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Services et Produits</h2>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setIsServiceFormOpen(true)} variant="outline">
            <Stethoscope className="h-4 w-4 mr-2" />
            Service
          </Button>
          <Button size="sm" onClick={() => setIsProductFormOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Produit
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{services.length}</div>
            <div className="text-sm text-gray-500">Services</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{products.length}</div>
            <div className="text-sm text-gray-500">Produits</div>
          </CardContent>
        </Card>
      </div>

      {/* Services Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5" />
            Mes Services
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {services.length > 0 ? (
              services.map((service) => (
                <div key={service.id} className="flex justify-between items-start p-3 border rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium">{service.service_name}</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {service.description || 'Aucune description'}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      {service.price && (
                        <span className="font-medium text-green-600">{service.price}€</span>
                      )}
                      {service.duration && (
                        <span className="text-gray-500">{service.duration} min</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Stethoscope className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Aucun service créé</p>
                <Button 
                  onClick={() => setIsServiceFormOpen(true)}
                  className="mt-4"
                  size="sm"
                >
                  Créer votre premier service
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Products Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Mes Produits
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {products.length > 0 ? (
              products.map((product) => (
                <div key={product.id} className="flex justify-between items-start p-3 border rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium">{product.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {product.description || 'Aucune description'}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <span className="font-medium text-green-600">{product.price}€</span>
                      {product.condition && (
                        <Badge variant="outline" className="text-xs">
                          {product.condition}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Aucun produit créé</p>
                <Button 
                  onClick={() => setIsProductFormOpen(true)}
                  className="mt-4"
                  size="sm"
                >
                  Créer votre premier produit
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <ServiceForm 
        open={isServiceFormOpen}
        onOpenChange={setIsServiceFormOpen}
      />

      <ProductForm 
        open={isProductFormOpen}
        onOpenChange={setIsProductFormOpen}
      />
    </div>
  );
};
